import { API_ENDPOINTS } from '../config/api.config';
import { apiGet } from '../utils/apiClient';

export interface NotificationDeliveryTrendPoint {
  date: string;
  delivered: number;
  failed: number;
  pending: number;
}

export interface NotificationDeliveryChannelRow {
  channel: string;
  total: number;
  delivered: number;
  failed: number;
  pending: number;
  avgDeliveryTimeMinutes: number;
  avgDeliveryTimeFormatted: string;
  averageTime: string;
}

export interface NotificationDeliverySummary {
  totalSent: number;
  deliverySuccessRate: number;
  pendingNotifications: number;
}

export interface NotificationDeliveryFilterOption {
  value: string;
  label: string;
}

export interface NotificationDeliveryFilterOptions {
  eventCategories: NotificationDeliveryFilterOption[];
  channels: NotificationDeliveryFilterOption[];
  statuses: NotificationDeliveryFilterOption[];
}

export interface NotificationDeliveryAnalyticsData {
  summary: NotificationDeliverySummary;
  trendPoints: NotificationDeliveryTrendPoint[];
  channelRows: NotificationDeliveryChannelRow[];
  filterOptions: NotificationDeliveryFilterOptions;
  meta?: {
    warnings?: string[];
  };
}

export interface NotificationDeliveryFilters {
  startDate: string;
  endDate: string;
  eventCategory?: string;
  channel?: string;
  status?: string;
  granularity?: 'day' | 'week' | 'month';
  facilityId?: string;
}

export interface NotificationDeliveryServiceResponse {
  success: boolean;
  data?: NotificationDeliveryAnalyticsData;
  error?: string;
  message?: string;
}

function num(value: unknown): number {
  return typeof value === 'number' && !Number.isNaN(value) ? value : Number(value) || 0;
}

function str(value: unknown, fallback = ''): string {
  if (value == null) {
    return fallback;
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return fallback;
}

function normalizeFilterOptions(raw: Record<string, unknown>): NotificationDeliveryFilterOptions {
  const mapOptions = (items: unknown): NotificationDeliveryFilterOption[] => {
    if (!Array.isArray(items)) {
      return [];
    }
    return items.map(item => {
      const row = (item as Record<string, unknown>) || {};
      return {
        value: str(row.value),
        label: str(row.label, str(row.value)),
      };
    });
  };

  return {
    eventCategories: mapOptions(raw.eventCategories ?? raw.event_categories),
    channels: mapOptions(raw.channels),
    statuses: mapOptions(raw.statuses),
  };
}

function normalizeSummary(raw: Record<string, unknown>): NotificationDeliverySummary {
  return {
    totalSent: num(raw.totalSent ?? raw.total_sent),
    deliverySuccessRate: num(raw.deliverySuccessRate ?? raw.delivery_success_rate),
    pendingNotifications: num(raw.pendingNotifications ?? raw.pending),
  };
}

function normalizeTrendPoints(raw: unknown): NotificationDeliveryTrendPoint[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.map(item => {
    const row = (item as Record<string, unknown>) || {};
    return {
      date: str(row.date),
      delivered: num(row.delivered),
      failed: num(row.failed),
      pending: num(row.pending),
    };
  });
}

function normalizeChannelRows(raw: unknown): NotificationDeliveryChannelRow[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.map(item => {
    const row = (item as Record<string, unknown>) || {};
    const avgMinutes = num(row.avgDeliveryTimeMinutes ?? row.avg_delivery_minutes);
    return {
      channel: str(row.channel),
      total: num(row.total),
      delivered: num(row.delivered),
      failed: num(row.failed),
      pending: num(row.pending),
      avgDeliveryTimeMinutes: avgMinutes,
      avgDeliveryTimeFormatted: str(
        row.avgDeliveryTimeFormatted ?? row.avg_delivery_time_formatted,
        avgMinutes > 0 ? `${avgMinutes} min` : '—'
      ),
      averageTime: str(
        row.averageTime ?? row.avgDeliveryTimeFormatted ?? row.avg_delivery_time_formatted,
        avgMinutes > 0 ? `${avgMinutes} min` : '—'
      ),
    };
  });
}

function normalizeAnalyticsData(raw: Record<string, unknown>): NotificationDeliveryAnalyticsData {
  const metaRaw = (raw.meta as Record<string, unknown>) || {};
  return {
    summary: normalizeSummary((raw.summary as Record<string, unknown>) || {}),
    trendPoints: normalizeTrendPoints(raw.trendPoints ?? raw.trend_points),
    channelRows: normalizeChannelRows(raw.channelRows ?? raw.channel_rows),
    filterOptions: normalizeFilterOptions(
      (raw.filterOptions as Record<string, unknown>) ||
        (raw.filter_options as Record<string, unknown>) ||
        {}
    ),
    meta: {
      warnings: Array.isArray(metaRaw.warnings) ? metaRaw.warnings.map(String) : [],
    },
  };
}

export async function getNotificationDeliveryAnalytics(
  filters: NotificationDeliveryFilters
): Promise<NotificationDeliveryServiceResponse> {
  try {
    const params = new URLSearchParams();
    params.set('start_date', filters.startDate);
    params.set('end_date', filters.endDate);
    if (filters.eventCategory) {
      params.set('event_category', filters.eventCategory);
    }
    if (filters.channel) {
      params.set('channel', filters.channel);
    }
    if (filters.status) {
      params.set('status', filters.status);
    }
    if (filters.granularity) {
      params.set('granularity', filters.granularity);
    }
    if (filters.facilityId) {
      params.set('facility_id', filters.facilityId);
    }

    const url = `${API_ENDPOINTS.ANALYTICS.NOTIFICATION_DELIVERY}?${params.toString()}`;
    const response = await apiGet<{
      success?: boolean;
      data?: Record<string, unknown>;
      error?: string;
      message?: string;
    }>(url, { requireAuth: true });

    const apiData = response.data;
    const payload = apiData?.data;

    if (payload && typeof payload === 'object' && apiData?.success !== false) {
      return {
        success: true,
        data: normalizeAnalyticsData(payload),
        message: apiData?.message,
      };
    }

    return {
      success: false,
      error: apiData?.error || 'Invalid response format',
    };
  } catch (err) {
    const message =
      err instanceof Error ? err.message : 'Failed to fetch notification delivery analytics';
    return {
      success: false,
      error: message,
    };
  }
}
